import React, { use, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { truncate } from "../utils/formatting";
import MetricLineChart from "./MetricChart";

const labelSummary = (obj) => {
  try {
    const entries = Object.entries(obj || {});
    if (!entries.length) return "labels";
    // prefer source/series_id if present
    const source = obj.source ? `src:${obj.source}` : null;
    const sid = obj.series_id ? `id:${obj.series_id}` : null;
    if (source || sid) return [source, sid].filter(Boolean).join(" · ");
    // otherwise show up to 2 key:value pairs
    return entries
      .slice(0, 2)
      .map(([k, v]) => `${k}:${String(v)}`)
      .join(" · ");
  } catch {
    return "labels";
  }
}

const Stat = ({ label, value }) => {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2">
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-white text-sm font-medium">{value}</dd>
    </div>
  );
}

export const MetricCard = ({
  name,
  description,
  firstTs,
  lastTs,
  latestTs,
  count,
  minValue,
  maxValue,
  avgValue,
  hasLabels,
  avgIntervalSeconds,
  pointsPerDay,
  latestValue,
  latestLabels,
  fmt,
  enableNavigate = false,
  truncateDesc = 2048,
  showChart = false,
  data = [],
}) => {
  // const gradientRing =
  //   "bg-[conic-gradient(at_top_right,_theme(colors.cyan.500),theme(colors.purple.500),theme(colors.cyan.500))]";

  const navigate = useNavigate();
  const [hoverEnabled, setHoverEnabled] = useState("hover:bg-gray-700/100");

  useEffect(() => {
    // console.log(`MetricCard: ${name} latestValue: ${latestValue}`);
    setHoverEnabled(enableNavigate ? "hover:bg-gray-700/100 cursor-pointer" : "");
  }, [enableNavigate]);

  // onclick to metric detail page?
  const handleClick = () => {
    // Navigate to the metric detail page
    console.log(`Clicked on metric: ${name}`);
    if (enableNavigate) {
      navigate(`/metrics/${name}`);
    }
  };

  return (
    <div className={["group relative rounded-2xl p-[4px] shadow/40 shadow-black/30"].join(" ")}>
      <div onClick={()=>handleClick()} className={`rounded-2xl h-full w-full bg-gray-800/95 ${hoverEnabled} p-6 sm:p-7 flex flex-col`}>
        {/* Header */}
        <div className="mb-1 flex items-center justify-between">
          <div>
            <h3 className="text-4xl font-semibold text-white mt-0">{name}</h3>
            <p className="text-xs text-gray-400">
              {fmt.fmtDate(firstTs)} → {fmt.fmtDate(lastTs)}
            </p>
          </div>
          <span
            className={[
              "text-[10px] sm:text-xs px-2 py-1 rounded-md",
              "border border-cyan-400/40 text-cyan-300 bg-cyan-400/10",
            ].join(" ")}
          >
            {fmt.fmtInt(count)} pts
          </span>
        </div>
        {showChart && 
          <div className="w-full h-[352px] mb-4">
            <MetricLineChart height={228} smoothness={0.2} data={data} />
          </div> }
        {/* Description */}
        <div>
          <p className="text-sm text-gray-300 mb-4">{truncate(description, truncateDesc) || <i>No description</i>}</p>
        </div>

        {/* Latest */}
        <div className="mb-4">
          <div className="text-sm text-gray-300">Latest</div>
          <div className="mt-1 flex flex-wrap items-baseline gap-2">
            <div className="text-2xl font-bold text-white">{fmt.fmtNum(latestValue)}</div>
            <div className="text-xs text-gray-400">{fmt.fmtDate(latestTs)}</div>
            {hasLabels && latestLabels ? (
              <div className="ml-auto text-[11px] rounded-md px-2 py-0.5 bg-purple-500/10 text-purple-300 border border-purple-500/30">
                {labelSummary(latestLabels)}
              </div>
            ) : null}
          </div>
        </div>

        {/* Stats grid */}
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Stat label="Min" value={fmt.fmtNum(minValue)} />
          <Stat label="Max" value={fmt.fmtNum(maxValue)} />
          <Stat label="Avg" value={fmt.fmtNum(avgValue)} />
          <Stat label="Pts / day" value={fmt.fmtNum(pointsPerDay, { maximumFractionDigits: 2 })} />
          <Stat label="Avg interval" value={fmt.humanInterval(avgIntervalSeconds)} />
          <Stat label="Labels" value={hasLabels ? "Yes" : "No"} />
        </dl>
      </div>
    </div>
  );
}